import { massageImageUrl } from "common/components/portable-renderers";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { SectionStyleProvider } from "webapp/ui/views/wire-container/wire-container";
import { WireMenuButton } from "webapp/ui/views/wire-menu-button/wire-menu-button";
import { breakoutActions, makeActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import type { UIStyleVariant } from "wire/types";
import { UIBackgroundStyle, UIButtonAppearance } from "wire/types";
import Img from "../img/img";
import type { CardProps } from "./card-collection-types";

export const Article: React.VFC<CardProps & { styleVariant: UIStyleVariant }> = p => {
    const { desc } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);
    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <div
                onClick={e => {
                    desc.onCardClick?.();
                    e.stopPropagation();
                }}
                className={desc.onCardClick !== undefined ? "card-click" : undefined}
                css={css`
                    &.card-click {
                        ${tw`hover:shadow-xl-dark`}
                    }
                `}
                tw="relative bg-n0 p-6 text-text-dark flex-col flex shadow-md w-full rounded-lg overflow-hidden
                    transition-shadow
                    gp-md:flex-row">
                {desc.image && (
                    <div
                        tw="hidden flex-shrink-0 mr-6
                            gp-md:block">
                        <Img
                            src={massageImageUrl(desc.image, { thumbnail: false, width: 208, height: 208 }, undefined)}
                            tw="object-cover bg-n50 rounded-lg w-20 h-20
                                gp-lg:(w-32 h-32)
                                gp-xl:(w-44 h-44)
                                gp-2xl:(w-52 h-52)"
                        />
                    </div>
                )}
                <div tw="flex-grow flex flex-col">
                    <div tw="flex">
                        {desc.image && (
                            <div
                                tw="flex-shrink-0 mr-6
                                    gp-md:hidden">
                                <Img
                                    src={massageImageUrl(
                                        desc.image,
                                        { thumbnail: false, width: 208, height: 208 },
                                        undefined
                                    )}
                                    tw="w-16 h-16 object-cover bg-n50 rounded-lg"
                                />
                            </div>
                        )}
                        <div tw="flex flex-col flex-1">
                            {desc.emphasis && (
                                <div
                                    tw="font-size[11px] line-height[20px] uppercase text-accent font-bold
                                        gp-md:font-size[12px]">
                                    {desc.emphasis}
                                </div>
                            )}
                            {desc.title && (
                                <h3
                                    tw="text-base font-extrabold whitespace-pre-wrap
                                        gp-md:text-lg
                                        gp-lg:text-xl
                                        gp-xl:text-2xl">
                                    {desc.title}
                                </h3>
                            )}
                            {desc.subtitle && (
                                <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>
                            )}
                        </div>
                    </div>
                    {desc.body && <p tw="text-sm mt-1.5 text-text-xpale whitespace-pre-wrap">{desc.body}</p>}
                    <div tw="flex mt-3" onClick={e => e.stopPropagation()}>
                        {primary !== undefined && (
                            <WireButton
                                data-testid="card-primary-button"
                                appearance={UIButtonAppearance.Filled}
                                {...makeActionSpreadProps(primary)}
                            />
                        )}
                        {secondary !== undefined && (
                            <WireButton
                                tw="not-first:ml-2"
                                appearance={UIButtonAppearance.Bordered}
                                {...makeActionSpreadProps(secondary)}
                            />
                        )}
                        {menuActions.length > 0 && (
                            <WireMenuButton
                                tw="not-first:ml-2"
                                appearance={UIButtonAppearance.Bordered}
                                menuItems={menuActions}
                            />
                        )}
                    </div>
                </div>
            </div>
        </SectionStyleProvider>
    );
};
